import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronLeft, ChevronRight, MoveHorizontal } from "lucide-react";
import type { ServiceCard } from "@/components/ServiceTile";

export function ServicesCarousel({
  items,
  title,
  subtitle,
}: {
  items: ServiceCard[];
  title: string;
  subtitle: string;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({ pitch: 0, cardW: 0, railW: 0 });
  const [index, setIndex] = useState(0);
  const [hintSeen, setHintSeen] = useState(false);
  const total = items.length;

  // Measure real card pitch so centering never depends on viewport-unit math.
  useEffect(() => {
    const rail = railRef.current;
    const track = trackRef.current;
    if (!rail || !track) return;
    const measure = () => {
      const cards = Array.from(track.children) as HTMLElement[];
      const first = cards[0];
      if (!first) return;
      const cardW = first.offsetWidth;
      const second = cards[1];
      const pitch = second ? second.offsetLeft - first.offsetLeft : cardW;
      setMetrics({ pitch, cardW, railW: rail.clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(rail);
    ro.observe(track);
    return () => ro.disconnect();
  }, [total]);

  const goTo = useCallback(
    (i: number) => {
      setHintSeen(true);
      setIndex(Math.min(Math.max(i, 0), total - 1));
    },
    [total],
  );

  // Wheel inside the rail moves one card at a time at a relaxed pace.
  const stateRef = useRef(index);
  stateRef.current = index;
  const lockRef = useRef(0);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const raw = horizontal ? e.deltaX : e.deltaY;
      const delta = raw * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
      if (Math.abs(delta) < 2) return;
      const dir = delta > 0 ? 1 : -1;
      const i = stateRef.current;
      e.preventDefault();
      e.stopPropagation();
      const now = performance.now();
      if (now - lockRef.current < 260) return;
      lockRef.current = now;
      // Wrap around at both ends.
      goTo((i + dir + total) % total);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [goTo, total]);

  // Touch drag left/right.
  const touchRef = useRef<{ x: number; y: number; handled: boolean } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, handled: false };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const s = touchRef.current;
    if (!s || s.handled) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    s.handled = true;
    goTo(index + (dx < 0 ? 1 : -1));
  };
  const onTouchEnd = () => {
    touchRef.current = null;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(index + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(index - 1);
    }
  };

  return (
    <section className="relative bg-primary py-16 text-primary-foreground [--card-gap:6vw] [--card-w:min(80vw,320px)] sm:[--card-gap:3vw] sm:[--card-w:min(60vw,300px)] lg:[--card-w:min(40vw,360px)] lg:py-20">
      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl text-balance">{title}</h2>
        <p className="mx-auto mt-4 max-w-3xl text-primary-foreground/85">{subtitle}</p>
        <p
          className={`mt-6 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary-foreground/70 transition-opacity duration-500 ${
            hintSeen ? "opacity-0" : "opacity-100"
          }`}
        >
          <MoveHorizontal className="h-3.5 w-3.5" />
          Scroll or drag to browse
        </p>
      </div>

      {/* Horizontal rail: its own interaction surface */}
      <div
        ref={railRef}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label={title}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onScroll={(e) => {
          // Focusing a card control can nudge this clipped track sideways; keep it pinned.
          e.currentTarget.scrollLeft = 0;
        }}
        className="relative mt-8 flex items-center overflow-clip outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
      >
        <div
          ref={trackRef}
          className="flex items-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
          style={{
            gap: "var(--card-gap)",
            transform: `translateX(${metrics.railW / 2 - metrics.cardW / 2 - index * metrics.pitch}px)`,
          }}
        >
          {items.map((item, i) => {
            const dist = Math.abs(i - index);
            const isCenter = dist === 0;
            return (
              <article
                key={item.title}
                aria-hidden={!isCenter}
                style={{
                  width: "var(--card-w)",
                  transform: `scale(${isCenter ? 1 : 0.88})`,
                  opacity: dist === 0 ? 1 : dist === 1 ? 0.6 : 0.3,
                }}
                className="relative aspect-[4/5] flex-shrink-0 overflow-hidden rounded-2xl bg-neutral-900 shadow-[0_30px_90px_-40px_rgba(0,0,0,0.6)] ring-1 ring-primary-foreground/10 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
                <Link
                  to={item.href}
                  aria-label={item.title}
                  className="absolute inset-0 z-0"
                  tabIndex={isCenter ? 0 : -1}
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 p-5 text-left text-white sm:p-6">
                  <h3 className="font-display text-xl font-semibold leading-tight sm:text-2xl">{item.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-white/80">{item.description}</p>
                  <div className="pointer-events-auto relative z-10 mt-4 flex flex-wrap items-center gap-2">
                    <Link
                      to="/contact"
                      tabIndex={isCenter ? 0 : -1}
                      className="inline-flex h-9 items-center rounded-md bg-accent px-3 text-[11px] font-bold uppercase tracking-wide text-accent-foreground transition-colors hover:opacity-90"
                    >
                      Get Quote
                    </Link>
                    <Link
                      to={item.href}
                      tabIndex={isCenter ? 0 : -1}
                      className="inline-flex h-9 items-center gap-1 rounded-md border border-white/40 bg-white/5 px-3 text-[11px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-white hover:text-primary"
                    >
                      Learn More<span className="sr-only"> about {item.title}</span>
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => goTo(index - 1)}
          disabled={index <= 0}
          aria-label="Previous service"
          className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-30 sm:left-6"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          disabled={index >= total - 1}
          aria-label="Next service"
          className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-30 sm:right-6"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-8 flex items-center justify-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.title}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to ${item.title}`}
            aria-current={i === index}
            className="flex h-6 items-center px-1"
          >
            <span
              className={`h-[3px] rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-accent" : "w-2 bg-primary-foreground/40"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
